import {PaymentProvider} from '@gatewee/types/provider';
import {Recipient} from './recipient';
import {PaymentMethod} from '@gatewee/types/payment';

export interface RecipientSetting {
  id: number;
  recipient: Recipient;
  key: string;
  value: string | Record<string, unknown>;
  secret: boolean;
  default?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export enum PayoutPolicy {
  PER_INCOME = 'per_income',
  FREE_BALANCE = 'free_balance',
}

export enum RecipientSettingKey {
  // DETERMINES IF WE'LL CREATE AUTOMATICALLY DAILY WITHDRAWALS
  ENABLE_AUTO_WITHDRAWAL = 'enableAutoWithdrawal',

  /**
   * Portion of withdrawable balance to retain when creating automatic scheduled
   * withdrawals (0–100). Default 10 when unset (legacy 90% payout).
   */
  AUTO_WITHDRAWAL_RETENTION_PCT = 'autoWithdrawalRetentionPct',

  // FALSES DETERMINES THAT WITHDRAWALS NEEDS MANUAL APPROVAL
  PRE_APPROVED_WITHDRAWAL = 'preApprovedWithdrawal',

  /** When true, recipient-initiated refunds skip domain approval and process immediately. */
  AUTO_APPROVE_REFUNDS = 'autoApproveRefunds',

  ENABLED_COMMUNICATIONS = 'enabledCommunications',

  TRACKING = 'tracking',

  PAYMENT_METHODS = 'paymentMethods',

  PROVIDERS_BASE_FEES = 'providersBaseFees',

  RETENTION_DEFINITION = 'retentionDefinition',

  PAYMENT_SPLIT_WHITELIST = 'paymentSplitWhitelist',

  PAYOUT_POLICY = 'payoutPolicy',

  ANTIFRAUD = 'antifraud',

  FEE_COLLECTION_POLICY = 'feeCollectionPolicy',

  PROVIDER_SETTINGS_OVERRIDES = 'providerSettingsOverrides',

  /** Overrides for provider capabilities. Applied to the recipient's provider. */
  PROVIDER_CAPABILITIES_OVERRIDE = 'providerCapabilitiesOverride',

  GLOBAL_WEBHOOK_URL = 'globalWebhookUrl',

  /** When true, automatic fee batch creation and processing run; when false, only manual processing via endpoints. Same key as domain for inheritance. */
  ENABLE_AUTO_FEE_COLLECTION = 'enableAutoFeeCollection',

  /** Global payment split rules (pix-key–scoped, external only). Value: GlobalPaymentSplitRule[]. */
  GLOBAL_PAYMENT_SPLIT_RULES = 'globalPaymentSplitRules',

  /** When true, the provider routing table is evaluated before falling back to recipient.provider. */
  ENABLE_PROVIDER_ROUTER = 'enableProviderRouter',

  /** When true, new bank accounts require domain approval before cash-out; same key as domain for inheritance. */
  REQUIRE_BANK_ACCOUNT_APPROVAL = 'requireBankAccountApproval',

  /** When true, the lost-transactions backfill CLI will process this recipient's unmatched PIX webhooks. */
  ENABLE_LOST_TX_BACKFILL = 'enableLostTxBackfill',

  /** Max invoice amount (BRL). 0 or unset = unlimited. */
  // TODO: Add validation for this setting
  INVOICE_AMOUNT_LIMIT = 'invoiceAmountLimit',

  /** Max single withdrawal amount (BRL). 0 or unset = unlimited. */
  // TODO: Add validation for this setting
  CASHOUT_TRANSACTION_LIMIT = 'cashoutTransactionLimit',

  /** Max sum of withdrawals per UTC calendar day (BRL). 0 or unset = unlimited. */
  DAILY_CASHOUT_LIMIT = 'dailyCashoutLimit',

  ENABLE_SETTLEMENT_INCOME_BATCH = 'enableSettlementIncomeBatch',

  /**
   * When true, Marlim TED-outside recipients get direct liquidation via the
   * daily Marlim conciliation job. Mutually exclusive with enableSettlementIncomeBatch.
   */
  ENABLE_MARLIM_DIRECT_LIQUIDATION = 'enableMarlimDirectLiquidation',

  INVOICE_AMOUNT_MINIMUM = 'invoiceAmountMinimum',

  /**
   * When true, requested withdrawal amount is net payout and fee is added on top.
   * Default false. Always on for EMV payouts.
   */
  ADD_WITHDRAWAL_FEE_TO_AMOUNT = 'addWithdrawalFeeToAmount',

  ENABLE_MASTER_PROVIDER_FEE_DEDUCTION = 'enableMasterProviderFeeDeduction',

  /**
   * Allowlist of PIX key kinds for free PIX payouts with inline bankAccount.
   * Same key as domain for inheritance (AND-merge). Unset = all kinds allowed.
   */
  ALLOWED_WITHDRAWAL_PIX_KEY_TYPES = 'allowedWithdrawalPixKeyTypes',

  /** When true, recipient can pay boletos (bank-slip bill payment / cash-out). */
  ENABLE_BANK_SLIP_PAYMENT = 'enableBankSlipPayment',
}

export enum FeeCollectionPolicy {
  INTERNAL = 'internal',
  PROVIDER_SPLIT = 'providerSplit',
}

export type RecipientFeeCollectionPolicy = {
  [key in PaymentProvider]?:
    | {
        [key in PaymentMethod]?: FeeCollectionPolicy;
      }
    | undefined;
};

export interface RecipientLimitsPeriod {
  timezone: string;
  dayStart: string;
  dayEnd: string;
}

export interface RecipientLimitsSnapshot {
  invoiceAmountLimit: number | null;
  invoiceAmountMinimum: number | null;
  cashoutTransactionLimit: number | null;
  dailyCashoutLimit: number | null;
  dailyCashoutUsed: number;
  dailyCashoutRemaining: number | null;
  scheduledWithdrawalLimit: number | null;
  period: RecipientLimitsPeriod;
}
